/**
 * @typedef {Object} ThumbnailToken
 * @property {string} sig - base64url HMAC signature, appended to thumbnail URLs verbatim
 * @property {number} exp - expiry as Unix seconds
 * @property {string} expiresAt - same instant as exp, ISO UTC
 * @property {string} scope - customer code the token is bound to
 * @property {string} baseUrl - ThumbnailGenerator endpoint
 */

const SKEW_MS = 10 * 60 * 1000; // refresh 10 min before actual expiry

let fetcher = null;
let token = null;
let refreshPromise = null;
const listeners = new Set();

/** Call once at app bootstrap with the app's authenticated API client. */
export function configureThumbnailToken(f) {
  fetcher = f;
}

/** Call on logout so the next login fetches a token for the new user/customer. */
export function clearThumbnailToken() {
  token = null;
  refreshPromise = null;
  emit();
}

export function getSnapshot() {
  return token;
}

export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function emit() {
  listeners.forEach((l) => l());
}

function isFresh(t) {
  return !!t && Date.parse(t.expiresAt) - SKEW_MS > Date.now();
}

/**
 * Returns a valid token, fetching one if missing or stale.
 * Stampede guard: concurrent callers (a grid mounting hundreds of thumbnails)
 * share ONE in-flight request via refreshPromise.
 *
 * @returns {Promise<ThumbnailToken>}
 */
export function getValidToken() {
  if (isFresh(token)) return Promise.resolve(token);
  return forceRefresh();
}

/**
 * Unconditional refresh (reactive path after an image 401). Also deduped.
 *
 * @returns {Promise<ThumbnailToken>}
 */
export function forceRefresh() {
  if (refreshPromise) return refreshPromise;
  if (!fetcher) return Promise.reject(new Error('thumbnail token fetcher not configured'));

  refreshPromise = fetcher()
    .then((t) => {
      token = t;
      emit();
      return t;
    })
    .finally(() => {
      refreshPromise = null; // success or failure: next caller may try again
    });

  return refreshPromise;
}
